import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ORDENABLE_PAGE_SIZE, REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useListService from '@/data/hook/useListService';
import { resourceQueries } from '@/data/query-keys/resourceQueryKey';
import {
  createLessonResource,
  removeLessonResource,
  reorderLessonResources,
  updateLessonResource,
} from '@/data/services/resourceServices';
import Resource, { ResourceDTO } from '@/models/Resource';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { reorder } from '@/utils/list';
import { OnUploadProgressFn } from '@/utils/shared/uploadProgress';

type UseResourcesProps = {
  lessonId: number;
};

type CreateMutationArgs = {
  body: Partial<ResourceDTO>;
  onUploadProgress?: OnUploadProgressFn;
};

type UpdateMutationArgs = {
  resourceId: number;
  data: Partial<ResourceDTO>;
  onUploadProgress?: OnUploadProgressFn;
};

const useResources = ({ lessonId }: UseResourcesProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.resourcesTab',
  });

  const {
    results: resources,
    isInitialLoading,
    cacheManager,
  } = useListService<Resource>({
    ...resourceQueries.lessonList(lessonId, { pageSize: ORDENABLE_PAGE_SIZE }),
    enabled: !!lessonId,
    staleTime: REQUEST_STALE_TIME_IN_MS,
    keepPreviousData: true,
  });

  const createMutation = useMutation({
    mutationFn: ({ body, onUploadProgress }: CreateMutationArgs) =>
      createLessonResource(lessonId, body, { onUploadProgress }),
    onSuccess: resource => {
      cacheManager.add(resource);
      alert.success(t('createSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ resourceId, data, onUploadProgress }: UpdateMutationArgs) =>
      updateLessonResource(lessonId, resourceId, data, { onUploadProgress }),
    onSuccess: resource => {
      cacheManager.update(resource);
      alert.success(t('updateSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (resourceId: number) =>
      removeLessonResource(lessonId, resourceId),
    onSuccess: (_, resourceId) => {
      cacheManager.remove(resourceId);
      alert.success(t('deleteSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const reorderMutation = useMutation({
    onMutate: (resourceIds: number[]) => {
      const reordenedResources = reorder(resources, resourceIds);
      cacheManager.set(reordenedResources);
    },
    mutationFn: (resourceIds: number[]) =>
      reorderLessonResources(
        lessonId,
        resourceIds.map(id => ({ id })),
      ),
    onSuccess: () => alert.success(t('reorderSuccess')),
    onError: err => {
      cacheManager.undo();
      alert.error(getErrorMessage(err));
    },
  });

  return {
    resources,
    isLoading: isInitialLoading,
    createResource: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateResource: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteResource: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
    reorderResources: reorderMutation.mutate,
    isReordering: reorderMutation.isPending,
  };
};

export default useResources;
